/**
 * Shared bits of the report charts: colors, number formats and the day axis.
 *
 * The colors were checked with the dataviz palette validator against the card
 * surface below (dark mode — the app is dark-only):
 *   paid / unpaid       #199e70, #c98500 — all checks pass (CVD ΔE 8.4)
 *   runs / contracts    #3987e5, #d95926 — all checks pass (CVD ΔE 26.8)
 * The table's lighter text green/amber (#66bb6a/#ffb74d) fail as fills — too
 * light for the band and too close under protanopia — so the marks use these.
 */
export const CHART = {
  surface: '#171b1b',
  paid: '#199e70',
  unpaid: '#c98500',
  series1: '#3987e5',
  series2: '#d95926',
  grid: '#2c2c2a',
  axis: '#383835',
  muted: '#898781',
  ink: '#ffffff',
  inkSecondary: '#c3c2b7',
};

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const full = new Intl.NumberFormat('en');

export const fmtCompact = (n: number): string => compact.format(n);
export const fmtFull = (n: number): string => full.format(n);

/** Round a max up to a clean axis top and return evenly spaced ticks from 0. */
export function niceTicks(max: number, count = 4): number[] {
  if (max <= 0) return [0];
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] < max) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}

/** Every YYYY-MM-DD from the earliest to the latest given day, so quiet days show as gaps. */
export function dayRange(dates: string[]): string[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].sort();
  const out: string[] = [];
  const end = Date.parse(sorted[sorted.length - 1] + 'T00:00:00Z');
  for (let t = Date.parse(sorted[0] + 'T00:00:00Z'); t <= end; t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/** "Mar 11" from "2026-03-11" — the day is already in the report's zone, so format it as UTC. */
export function shortDay(date: string): string {
  return new Date(date + 'T00:00:00Z').toLocaleDateString('en', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Indices of at most `max` evenly spaced x labels, always including the last. */
export function labelIndices(n: number, max: number): number[] {
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil((n - 1) / (max - 1));
  const out: number[] = [];
  for (let i = n - 1; i >= 0; i -= step) out.unshift(i);
  return out;
}
